/**
 * Lazy cell tree selector. Used both by the cell filter (FacetsPanel) and by the
 * FillPanel ("закинуть ячейку").
 *
 * Roots load via useCells({}) (no parent_id). Expanding node N loads its direct
 * children via useCells({ parentId: N }) — a per-node query, fetched the first
 * time the node is opened. An empty children response marks the node a leaf
 * (the expander arrow stays but reveals nothing).
 *
 * "Включая дочерние" is one checkbox for the whole selector (not per-node).
 *
 * single mode: picking a node replaces value with [id]; multi: toggles id in the set.
 *
 * Search ("Найти ячейку") queries OUR backend's flat cell index
 * (GET /api/cells/search via useCellSearch), NOT mob's ?title= (which returns an id
 * path, not cell objects, and hangs on Cyrillic). A non-empty debounced query swaps
 * the tree for a flat result list; clearing the box restores the tree.
 */

import * as React from "react";
import { ChevronRight, Search } from "lucide-react";

import type { Cell } from "@/api/client";
import { useCellSearch, useCells } from "@/api/hooks";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { cn } from "@/lib/utils";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";

export interface CellTreeSelectProps {
  mode: "single" | "multi";
  /** Selected cell ids (single -> length 0 or 1). */
  value: number[];
  /** "Включая дочерние" checkbox (one per selector). */
  includeChildren: boolean;
  onChange: (value: number[], includeChildren: boolean) => void;
  /** Optional: hide the "включая дочерние" checkbox (e.g. for "eq"-only search). */
  hideIncludeChildren?: boolean;
  className?: string;
}

function cellLabel(c: Cell): string {
  return c.title || `#${c.id}`;
}

export function CellTreeSelect({
  mode,
  value,
  includeChildren,
  onChange,
  hideIncludeChildren = false,
  className,
}: CellTreeSelectProps) {
  const selected = React.useMemo(() => new Set(value), [value]);

  const [search, setSearch] = React.useState("");
  const debounced = useDebouncedValue(search.trim(), 300);
  const searching = debounced.length > 0;

  function pick(id: number) {
    if (mode === "single") {
      onChange([id], includeChildren);
      return;
    }
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange(Array.from(next), includeChildren);
  }

  return (
    <div className={cn("space-y-2", className)}>
      <div className="relative">
        <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Найти ячейку"
          className="h-8 pl-8"
        />
      </div>

      <div className="max-h-64 overflow-y-auto rounded-md border bg-card p-1 text-sm">
        {searching ? (
          <SearchResults q={debounced} selected={selected} onPick={pick} />
        ) : (
          <CellLevel
            parentId={null}
            depth={0}
            mode={mode}
            selected={selected}
            onPick={pick}
          />
        )}
      </div>

      {!hideIncludeChildren && (
        <label className="flex cursor-pointer items-center gap-2 text-xs text-muted-foreground">
          <Checkbox
            checked={includeChildren}
            onCheckedChange={(c) => onChange(value, c)}
          />
          Включая дочерние ячейки
        </label>
      )}
    </div>
  );
}

/**
 * Flat search results from OUR backend's cell index (useCellSearch). Rows are a
 * checkbox + title (no expander) and pick via onPick, so single/multi and the
 * selected set behave exactly like tree nodes.
 */
function SearchResults({
  q,
  selected,
  onPick,
}: {
  q: string;
  selected: Set<number>;
  onPick: (id: number) => void;
}) {
  const query = useCellSearch(q, true);

  if (query.isPending) {
    return (
      <div className="flex items-center gap-2 px-2 py-1.5 text-muted-foreground">
        <Spinner /> Поиск…
      </div>
    );
  }
  if (query.isError) {
    return <div className="px-2 py-1.5 text-destructive">Ошибка поиска.</div>;
  }
  const items = query.data?.results ?? [];
  if (items.length === 0) {
    return <div className="px-2 py-1.5 text-muted-foreground">Ничего не найдено.</div>;
  }
  return (
    <div className="space-y-0.5">
      {items.map((c) => (
        <label
          key={c.id}
          className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 hover:bg-accent"
        >
          <Checkbox checked={selected.has(c.id)} onCheckedChange={() => onPick(c.id)} />
          <span className="truncate" title={cellLabel(c)}>{cellLabel(c)}</span>
        </label>
      ))}
    </div>
  );
}

/** One level of the tree: the children of `parentId` (null = roots). */
function CellLevel({
  parentId,
  depth,
  mode,
  selected,
  onPick,
}: {
  parentId: number | null;
  depth: number;
  mode: "single" | "multi";
  selected: Set<number>;
  onPick: (id: number) => void;
}) {
  const params = parentId == null ? {} : { parentId };
  const query = useCells(params);

  if (query.isPending) {
    return (
      <div className="flex items-center gap-2 px-2 py-1.5 text-muted-foreground">
        <Spinner /> Загрузка…
      </div>
    );
  }
  if (query.isError) {
    return <div className="px-2 py-1.5 text-destructive">Не удалось загрузить ячейки.</div>;
  }
  const items = query.data ?? [];
  if (items.length === 0) {
    if (parentId == null) {
      return <div className="px-2 py-1.5 text-muted-foreground">Ячеек нет.</div>;
    }
    return null;
  }
  return (
    <div className="space-y-0.5">
      {items.map((c) => (
        <CellNode
          key={c.id}
          cell={c}
          depth={depth}
          mode={mode}
          selected={selected}
          onPick={onPick}
        />
      ))}
    </div>
  );
}

/** A single tree node: checkbox + expander; lazily renders its level when open. */
function CellNode({
  cell,
  depth,
  mode,
  selected,
  onPick,
}: {
  cell: Cell;
  depth: number;
  mode: "single" | "multi";
  selected: Set<number>;
  onPick: (id: number) => void;
}) {
  const [open, setOpen] = React.useState(false);
  // mob may expose has_children; otherwise assume expandable and let the lazy
  // child load reveal that it's a leaf (empty response renders nothing).
  const hasChildren = cell.has_children;
  const expandable = hasChildren !== false;

  return (
    <div>
      <div
        className="flex items-center gap-1.5 rounded px-1 py-1 hover:bg-accent"
        style={{ paddingLeft: depth * 14 + 4 }}
      >
        {expandable ? (
          <button
            type="button"
            className="flex h-4 w-4 shrink-0 items-center justify-center text-muted-foreground"
            onClick={() => setOpen((o) => !o)}
            aria-label={open ? "Свернуть" : "Развернуть"}
          >
            <ChevronRight
              className={cn("h-3.5 w-3.5 transition-transform", open && "rotate-90")}
            />
          </button>
        ) : (
          <span className="inline-block h-4 w-4 shrink-0" />
        )}

        <label className="flex min-w-0 flex-1 cursor-pointer items-center gap-2">
          <Checkbox checked={selected.has(cell.id)} onCheckedChange={() => onPick(cell.id)} />
          <span className="truncate" title={cellLabel(cell)}>{cellLabel(cell)}</span>
        </label>
      </div>

      {open && expandable && (
        <CellLevel
          parentId={cell.id}
          depth={depth + 1}
          mode={mode}
          selected={selected}
          onPick={onPick}
        />
      )}
    </div>
  );
}
